import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { ConfirmDialogService } from '../../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../../notification/notification.service';
import { UpdateWorktimeConfigRequest, WorktimeOverviewRow, WorktimeService } from '../../api';
import { WT_MONTH_NAMES, WorktimeBase, WtEmploymentType, wtAvatarColor, wtErrorText, wtFt, wtHL, wtInitial } from '../worktime-shared';

// ============================================================================
// Munkaidő admin — user-választó, bármely user havi nézete + globális konfig.
// Design: erp-design/screens/08-munkaido.html (wtIsAdmin ág),
// logika: 23-logic.js worktimeVals() — közös rész a worktime-shared.ts-ben.
// ============================================================================

/** GET users — csak a chipekhez kellő mezők (nem importáljuk a backend entityt). */
interface WtUserDto {
  id?: number;
  username?: string;
  full_name?: string;
  enabled?: number;
  employment_type?: string;
}

interface WtUserChipVm {
  id: number;
  name: string;
  initial: string;
  color: string;
  /** Külsős munkatárs (employment_type = 'external'). */
  external: boolean;
}

/** Bot / szolgáltatás-felhasználók — nincs munkaidejük (backend: chat BOT_USERNAMES + HIDDEN_USERNAMES). */
const WT_NON_HUMAN_USERNAMES = ['foxi', 'boris', 'farmy', 'printersline'];

type WtCfgKey = 'dailyRequiredHours' | 'annualVacationDays' | 'vacationDayHours' | 'hourlyWage';

/** Konfig hiányában mutatott alapértékek (a backend párhuzamosan készül). */
const CFG_DEFAULTS: Record<WtCfgKey, number> = {
  dailyRequiredHours: 8,
  annualVacationDays: 25,
  vacationDayHours: 8,
  hourlyWage: 0,
};

@Component({
  selector: 'app-munkaido-admin',
  templateUrl: './munkaido-admin.component.html',
  styleUrl: './munkaido-admin.component.scss'
})
export class MunkaidoAdminComponent extends WorktimeBase implements OnInit {

  users: WtUserChipVm[] = [];
  selectedUserId: number | null = null;
  settingsOpen = false;

  /** Havi összesítő táblázat — mindenki egy sorban. */
  overview: WorktimeOverviewRow[] = [];
  overviewLoading = false;
  readonly ft = wtFt;
  readonly hl = wtHL;

  /** wtCfgFields — 23-logic.js 1:1 (sorrend és feliratok a prototípusból). */
  readonly cfgFields: { key: WtCfgKey; label: string; unit: string }[] = [
    { key: 'dailyRequiredHours', label: 'Kötelező napi óraszám', unit: 'óra / nap' },
    { key: 'annualVacationDays', label: 'Éves szabadság', unit: 'nap / év' },
    { key: 'vacationDayHours', label: 'Szabadnap elszámolása', unit: 'óra bér / szabadnap' },
    { key: 'hourlyWage', label: 'Órabér', unit: 'Ft / óra' },
  ];

  /** Külsősnél a szabadsághoz kötődő beállítások értelmetlenek — elrejtjük. */
  get visibleCfgFields(): { key: WtCfgKey; label: string; unit: string }[] {
    if (!this.isExternal) { return this.cfgFields; }
    return this.cfgFields.filter(f => f.key === 'dailyRequiredHours' || f.key === 'hourlyWage');
  }

  constructor(
    worktime: WorktimeService,
    auth: AuthService,
    notification: NotificationService,
    confirmDialog: ConfirmDialogService,
    private http: HttpClient,
  ) {
    super(worktime, auth, notification, confirmDialog);
  }

  ngOnInit(): void {
    this.http.get<WtUserDto[]>('users').pipe(
      catchError(() => of([] as WtUserDto[])),
    ).subscribe(list => this.initUsers(list));
  }

  private initUsers(list: WtUserDto[]): void {
    this.users = (Array.isArray(list) ? list : [])
      .filter(u => u.id != null && u.enabled !== 0
        && !WT_NON_HUMAN_USERNAMES.includes((u.username || '').toLowerCase()))
      .map(u => this.toChip(
        u.id as number,
        u.full_name || u.username || ('#' + u.id),
        u.employment_type === 'external',
      ));

    const me = this.auth.user;
    if (this.users.length === 0 && me?.id != null) {
      // Üres/elérhetetlen users lista — legalább a bejelentkezett admin látszódjon.
      this.users = [this.toChip(me.id, me.full_name || me.username, false)];
    }

    // Alapértelmezett kiválasztás: a bejelentkezett user (prototípus: wtUser = supMe).
    const meChip = this.users.find(u => u.id === me?.id);
    const selected = meChip || this.users[0];
    this.selectedUserId = selected?.id ?? me?.id ?? null;
    this.employmentType = selected ? this.typeOf(selected) : null;
    this.reload();
  }

  private toChip(id: number, name: string, external: boolean): WtUserChipVm {
    return { id, name, initial: wtInitial(name), color: wtAvatarColor(name), external };
  }

  private typeOf(u: WtUserChipVm): WtEmploymentType {
    return u.external ? 'external' : 'employee';
  }

  protected override targetUserId(): number | undefined {
    return this.selectedUserId ?? undefined;
  }

  override reload(): void {
    super.reload();
    this.loadOverview();
  }

  private overviewSeq = 0;

  private loadOverview(): void {
    const seq = ++this.overviewSeq;
    this.overviewLoading = true;
    this.worktime.getOverview(this.year, this.month + 1).pipe(
      catchError(() => of([] as WorktimeOverviewRow[])),
    ).subscribe(rows => {
      if (seq !== this.overviewSeq) { return; }
      this.overview = rows || [];
      this.overviewLoading = false;
    });
  }

  get overviewTotals(): { worked: number; vacation: number; sick: number; absence: number; salary: number } {
    return this.overview.reduce((t, r) => ({
      worked: t.worked + r.workedHours,
      vacation: t.vacation + r.vacationDaysInMonth,
      sick: t.sick + r.sickDaysInMonth,
      absence: t.absence + r.absenceHoursInMonth,
      salary: t.salary + r.expectedGrossSalary,
    }), { worked: 0, vacation: 0, sick: 0, absence: 0, salary: 0 });
  }

  pickOverviewRow(row: WorktimeOverviewRow): void {
    const chip = this.users.find(u => u.id === row.userId);
    if (chip) { this.pickUser(chip); }
  }

  /** Havi összesítő letöltése CSV-ben (Excelhez: pontosvessző, BOM). */
  exportOverview(): void {
    const head = ['Név', 'Típus', 'Ledolgozott óra', 'Kötelező óra', 'Munkanapok', 'Hiányzó vég', 'Szabadság (nap)',
      'Betegszabadság (nap)', 'Távollét (óra)', 'Távollét egész nap', 'Maradék szabadság', 'Órabér', 'Várható bruttó bér'];
    const num = (n: number) => String(Math.round(n * 100) / 100).replace('.', ',');
    const lines = this.overview.map(r => [
      r.name, r.employmentType === 'external' ? 'Külsős' : 'Alkalmazott', num(r.workedHours), num(r.requiredHours),
      r.daysWorked, r.openDays, r.vacationDaysInMonth, r.sickDaysInMonth, num(r.absenceHoursInMonth),
      r.absenceDaysInMonth, r.employmentType === 'external' ? '' : r.vacationDaysLeft, r.hourlyWage, r.expectedGrossSalary,
    ].map(v => '"' + String(v).replace(/"/g, '""') + '"').join(';'));
    const csv = '\uFEFF' + [head.join(';'), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `munkaido-${this.year}-${String(this.month + 1).padStart(2, '0')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  get overviewTitle(): string {
    return 'Havi összesítő — ' + this.year + '. ' + WT_MONTH_NAMES[this.month];
  }

  pickUser(u: WtUserChipVm): void {
    if (u.id === this.selectedUserId) { return; }
    this.selectedUserId = u.id;
    // A konfig user-specifikus — ne maradjon az előző useré.
    this.config = null;
    this.summary = null;
    // A user-listából már tudjuk a típust — a summary majd megerősíti.
    this.employmentType = this.typeOf(u);
    this.closeDay();
    this.reload();
  }

  toggleSettings(): void {
    this.settingsOpen = !this.settingsOpen;
  }

  get selectedName(): string {
    const chip = this.users.find(u => u.id === this.selectedUserId);
    if (chip) { return chip.name; }
    const me = this.auth.user;
    return me ? (me.full_name || me.username) : '';
  }

  get settingsTitle(): string {
    return this.selectedName + ' beállításai (admin)';
  }

  cfgValue(key: WtCfgKey): number {
    const c = this.config;
    const v = c ? c[key] : null;
    return v != null ? v : CFG_DEFAULTS[key];
  }

  onCfgChange(key: WtCfgKey, event: Event): void {
    const input = event.target as HTMLInputElement;
    // 23-logic.js: Math.max(0, parseFloat(value) || 0)
    const value = Math.max(0, parseFloat(input.value) || 0);
    input.value = String(value);
    const request: UpdateWorktimeConfigRequest = {};
    request[key] = value;
    const userId = this.targetUserId();
    if (userId != null) { request.userId = userId; }
    this.worktime.updateConfig(request).subscribe({
      next: config => {
        this.config = config;
        this.notification.show('Beállítás mentve.', 'success');
        // A bér / kötelező óra KPI a konfigtól függ — friss összesítőt kérünk.
        this.reload();
      },
      error: (err) => this.notification.show(wtErrorText(err, 'A beállítás mentése nem sikerült.'), 'danger'),
    });
  }
}
